/** Seeded volcanic activity on the existing shared world clock. No accumulated
 * simulation state: joining, seeking time and frame rate do not restart a vent. */
import type {PlanetSampler} from './universe.js';
import type {VolcanoDef} from './volcanoes.js';
import * as pmath from './pmath.js';

export const VOLCANO_BUILDUP_S=12;
export const VOLCANO_ERUPTION_END_S=44;
export const VOLCANO_SETTLE_END_S=100;
export const VOLCANO_MAX_HEAT_HP_S=12;
export const VOLCANO_EJECTA_SLOTS=128;
const smooth=(x:number)=>{const v=Math.max(0,Math.min(1,x));return v*v*(3-2*v);};
const fraction=(seed:number,index:number)=>{let n=(seed^Math.imul(index+1,0x9e3779b9))>>>0;n=Math.imul(n^(n>>>16),0x85ebca6b);n=Math.imul(n^(n>>>13),0xc2b2ae35);return((n^(n>>>16))>>>0)/4294967296;};
export interface VolcanoActivity {phase:number;period:number;stage:'quiet'|'buildup'|'erupting'|'settling';warning:number;fountain:number;heat:number;plume:number}
export function volcanoPeriod(v:VolcanoDef):number{return 480+(v.seed>>>0)%241;}
export function volcanoPhaseOffset(v:VolcanoDef):number{return fraction(v.seed,701)*volcanoPeriod(v);}
export function createVolcanoActivity():VolcanoActivity{return{phase:0,period:0,stage:'quiet',warning:0,fountain:0,heat:0,plume:.18};}
export function volcanoActivity(v:VolcanoDef,seconds:number,out:VolcanoActivity):VolcanoActivity {
 const period=volcanoPeriod(v),phase=(((seconds%period)+volcanoPhaseOffset(v))%period+period)%period;
 out.phase=phase;out.period=period;
 out.stage=phase<VOLCANO_BUILDUP_S?'buildup':phase<VOLCANO_ERUPTION_END_S?'erupting':phase<VOLCANO_SETTLE_END_S?'settling':'quiet';
 out.warning=smooth(phase/VOLCANO_BUILDUP_S)*(1-smooth((phase-VOLCANO_ERUPTION_END_S)/28));
 out.fountain=smooth((phase-VOLCANO_BUILDUP_S)/3)*(1-smooth((phase-(VOLCANO_ERUPTION_END_S-8))/8));
 out.heat=smooth((phase-VOLCANO_BUILDUP_S)/6)*(1-smooth((phase-VOLCANO_ERUPTION_END_S)/28));
 out.plume=.18+.4*out.warning+.8*out.fountain;
 return out;
}
export interface VolcanicExposure {rate:number;warning:number;buildup:number;rumble:number;vent:number}
export function createVolcanicExposure():VolcanicExposure{return{rate:0,warning:0,buildup:0,rumble:0,vent:-1};}
const activity=createVolcanoActivity();
/** Local crater air/radiant heat. No distant ash damage or stacking with lava.
 * Height below the vent and terrain along the sightline shield the lower flanks. */
export function volcanicExposure(sampler:PlanetSampler,x:number,y:number,z:number,seconds:number,out:VolcanicExposure):VolcanicExposure {
 out.rate=0;out.warning=0;out.buildup=0;out.rumble=0;out.vent=-1;
 if(sampler.def.archetype!=='volcanic')return out;
 const features=sampler.volcanoes(),R=sampler.def.radius;
 for(let i=0;i<features.length;i++) {
  const v=features[i],r=R+v.poolM,dx=x-v.origin[0]*r,dy=y-v.origin[1]*r,dz=z-v.origin[2]*r;
  const h=dx*v.origin[0]+dy*v.origin[1]+dz*v.origin[2];
  const distance=Math.sqrt(dx*dx+dy*dy+dz*dz);
  if(distance>v.radius*3)continue;
  volcanoActivity(v,seconds,activity);
  const rumble=(.025+.34*activity.warning+.60*activity.fountain)*(1-smooth(distance/(v.radius*3)));
  if(rumble>out.rumble){out.rumble=rumble;out.vent=i;}
  const side=Math.sqrt(Math.max(0,distance*distance-h*h));
  const k=(1-smooth((side/v.crater-.65)/1.1))*smooth((h+2)/5)*(1-smooth((h-v.height*.26)/(v.height*.30+25)));
  if(k<=0)continue;
  // A visible buildup is also reported just outside the heated core, so the
  // HUD can warn before the first additional damage tick.
  out.warning=Math.max(out.warning,activity.warning*k);
  if(activity.stage==='buildup')out.buildup=Math.max(out.buildup,activity.warning*k);
  if(activity.heat<=0)continue;
  let exposed=true;
  const sx=v.origin[0]*(r+24),sy=v.origin[1]*(r+24),sz=v.origin[2]*(r+24);
  for(let j=1;j<=8;j++) {
   const t=j/9,px=sx+(x-sx)*t,py=sy+(y-sy)*t,pz=sz+(z-sz)*t;
   const length=Math.sqrt(px*px+py*py+pz*pz);
   if(length<sampler.surfaceRadius(px/length,py/length,pz/length)-.15){exposed=false;break;}
  }
  if(exposed)out.rate=Math.max(out.rate,VOLCANO_MAX_HEAT_HP_S*activity.heat*k);
 }
 return out;
}

export interface VolcanoEjectum {x:number;y:number;z:number;vx:number;vy:number;vz:number;size:number;heat:number;visible:boolean}
const birthActivity=createVolcanoActivity();
/** Cosmetic molten fragments: analytic parabolas which launch and land inside
 * the lava pool. They never become unregistered rocks or independent hitboxes. */
export function volcanoEjectum(v:VolcanoDef,slot:number,seconds:number,out:VolcanoEjectum):VolcanoEjectum {
 const seed=v.seed,lifetime=5+fraction(seed,slot*5)*4,period=lifetime+1.5;
 const local=seconds+fraction(seed,slot*5+1)*period,cycle=Math.floor(local/period),age=local-cycle*period;
 const birth=seconds-age;volcanoActivity(v,birth,birthActivity);
 out.visible=age<lifetime&&birthActivity.fountain>fraction(seed,slot*5+2)*.7;
 const t=Math.min(1,age/lifetime),angle=fraction(seed,slot*5+3)*pmath.TWO_PI;
 const run=v.poolRadius*(.12+.45*fraction(seed,slot*5+4));
 const peak=Math.max(22,v.height*(.27+.17*fraction(seed,slot*5+5)));
 out.x=pmath.cos(angle)*run*(t+.05);out.z=pmath.sin(angle)*run*(t+.05);
 out.y=4*peak*t*(1-t);
 out.vx=pmath.cos(angle)*run/lifetime;out.vz=pmath.sin(angle)*run/lifetime;out.vy=4*peak*(1-2*t)/lifetime;
 out.size=Math.max(.35,Math.min(3.2,v.radius*.0017))*(.55+fraction(seed,slot*5+6));
 out.heat=smooth(t/.05)*(1-smooth((t-.8)/.2))*birthActivity.fountain;
 return out;
}
